"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import Badge2026 from "@/components/sections/2026/ui/Badge2026";
import ResponsiveCopy from "@/components/sections/2026/ui/ResponsiveCopy";

// Section eyebrow above the diagram — same glass badge → headline → subtitle
// pattern (and rise-on-view stagger) as the Hero / Portfolio headers.
//
// The hidden state is the same whether or not the visitor asks for reduced
// motion — it is what the server writes into the HTML, and the server cannot
// know that preference, so branching it is a hydration mismatch. Reduced motion
// lives in the transition: the travel gets zero duration, the fade still plays.
const HIDDEN = { opacity: 0, y: 24 };

const REDUCED = { opacity: { duration: 0.3 }, y: { duration: 0 } };

// Shared by both halves of the subtitle's responsive pair. `hover`/`tap` swap
// the verb by input type, which is a different axis from the width split above
// — a phone gets "tap", a desktop "hover", and each side of the md breakpoint
// has its own copy on top of that.
const subtitleTags = {
  hover: (chunks) => <span className="hidden sm:inline">{chunks}</span>,
  tap: (chunks) => <span className="sm:hidden">{chunks}</span>,
};

export default function TechHeader() {
  const t = useTranslations("home.builtOnModern");
  const reduce = useReducedMotion();
  const variants = {
    hidden: HIDDEN,
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: reduce
        ? REDUCED
        : { duration: 0.5, delay: i * 0.12, ease: "easeOut" },
    }),
  };
  const reveal = {
    initial: "hidden",
    whileInView: "show",
    viewport: { once: true, amount: 0.4 },
    variants,
  };

  // Same gutters as the tree below it. `md:px-0` was safe only while the
  // viewport was wider than the 1033px column: at 768 the header ran to both
  // edges while the tree kept its padding.
  return (
    <div className="mx-auto mb-16 max-w-[1033px] px-6 md:mb-24 md:px-10 w-full">
      <div className="flex max-w-[800px] w-full flex-col items-start gap-[18px]">
        <motion.div {...reveal} custom={0}>
          <Badge2026>{t("badge")}</Badge2026>
        </motion.div>

        <motion.h2
          {...reveal}
          custom={1}
          className="text-3xl font-bold text-white th:leading-[1.35] lg:text-[36px]"
        >
          <ResponsiveCopy
            desktop={t("headline")}
            mobile={t("headlineMobile")}
          />
        </motion.h2>

        <motion.p
          {...reveal}
          custom={2}
          className="text-lg font-normal text-white/80 lg:text-xl"
        >
          <ResponsiveCopy
            desktop={t.rich("subtitle", subtitleTags)}
            mobile={t.rich("subtitleMobile", subtitleTags)}
          />
        </motion.p>
      </div>
    </div>
  );
}
